import { Tween } from "../Tween";
import { IObjectConfig } from "../baseConfigs/layoutConfigShema";
import Layout from "../utils/Layout";
import VisualUtil from "../utils/VisualUtil";
import BaseView from "./BaseView";
import { Filter, Sprite, Graphics, Loader, AnimatedSprite } from "pixi.js";
import ConsoleUtil from "../utils/ConsoleUtil";
import { ConfigurationTypes } from "../const/ConfigurationTypes";
import ConfigManager from "../managers/ConfigManager";
import ISymbolSpineAnimConfig from "../managers/contracts/configurations/ISymbolSpineAnimConfig";
import GameLoader from "../utils/GameLoader";
import SymbolSpineAnimation from "./SymbolSpineAnimation";
import { addEmitHelper } from "typescript";
import App from "../App";
import GameEvents from "../const/GameEvents";
import Configs from "../../../src/vendorConfigs/Configs";

/**
 * Abstraction for a single symbol.
 * A reel contains several of those.
 */
class ReelSymbol extends BaseView {
    // TODO COPY USEFUL LOGIC FROM EXTENDED FRAMEWORK INCLUDING DRAGON BONES INTEGRATION
    public id: number;
    public positionDefinitions: Map<string, IObjectConfig>;
    public zOrder: number;
    public reelIdx: number;
    public isFinal: boolean;
    protected bg: Sprite;
    protected bgBlur: Sprite;
    public symbol: Sprite;
    protected symbolBlur: Sprite;
    protected debugTag: Graphics;
    // protected frame: Sprite;
    protected shineFilter: Filter;
    // protected symAnim: AnimatedSprite;
    protected bounds: IObjectConfig;
    public forceHidden: boolean = false;
    public fakeId: number | null = null;
    public spineAnim: SymbolSpineAnimation = null;
    public isSpine: boolean;
    private spineBlurLevel: number = 0;

    constructor() {
        super();
        // this.removeListener('removed', this.destroy, this);
    }

    create(
        symBounds: IObjectConfig,
        positionDefinitions: Map<string, IObjectConfig>
    ) {
        this.isSpine = Configs.game.symbolType.spine;
        this.bounds = symBounds;
        this.positionDefinitions = positionDefinitions;
        this.debugTag = new Graphics();
        this.debugTag.lineStyle(3, 0xff0000, 1);
        this.debugTag.drawRect(
            -Layout.scalePixels(symBounds.width) / 2,
            -Layout.scalePixels(symBounds.height) / 2,
            Layout.scalePixels(symBounds.width),
            Layout.scalePixels(symBounds.height)
        );

        if (App.instance.debugBounds) {
            this.showDebugTag();
        }

        App.dispatcher.on(GameEvents.UI_UPDATE, () => {
            if (App.instance.debugBounds) {
                this.showDebugTag();
            } else {
                this.hideDebugTag();
            }
        }, this);

        if (this.isSpine) {
            this.createSpineAnim();
        } else {
            this.symbol = this.assets.getSprite("sym_empty");
            this.addChild(this.symbol);
            Layout.testSetHeight(this.symbol, symBounds.height);

            if (this.config.symbolBlur) {
                this.symbolBlur = this.assets.getSprite("sym_empty");
                this.addChild(this.symbolBlur);
                this.symbolBlur.alpha = 0;
                Layout.testSetHeight(this.symbolBlur, symBounds.height);
            }

            const uniform = {
                position: { type: "f", value: 0 },
                intensity: { type: "f", value: 1 },
            };

            if (Loader.shared.resources.shine) {
                this.shineFilter = new Filter(
                    null,
                    Loader.shared.resources.shine.data,
                    uniform
                );
            }

            this.symbol.filters = [];
        }
    }

    protected createSpineAnim = (): void => {
        let config = (ConfigManager.getConfig((ConfigurationTypes.SYMBOL_SPINE_ANIMATIONS)) as ISymbolSpineAnimConfig);
        let anim = new SymbolSpineAnimation(GameLoader.loader.resources[`spine~${config.name}~json`].spineData, config.name, config);
        const scaleFactorX = this.bounds.width / this.positionDefinitions.get("sym").width;
        const scaleFactorY = this.bounds.height / this.positionDefinitions.get("sym").height;
        const symbolScaleMultiplierX = !Number.isNaN(Number(App.config.symbolScaleX)) ? Number(App.config.symbolScaleX) : 1;
        const symbolScaleMultiplierY = !Number.isNaN(Number(App.config.symbolScaleY)) ? Number(App.config.symbolScaleY) : 1;
        anim.scale.set(Layout.globalScale * scaleFactorX * symbolScaleMultiplierX, Layout.globalScale * scaleFactorY * symbolScaleMultiplierY);
        this.spineAnim = anim;
        this.addChild(anim as any);
    }

    public showDebugTag() {
        if(this.debugTag.parent){
            this.debugTag.alpha/=2;
        }
        this.addChild(this.debugTag)
    }


    public hideDebugTag() {
        if(this.debugTag.parent) {
            this.debugTag.parent.removeChild(this.debugTag);
            this.debugTag.alpha=1;
        }
    }


    public get cellHeight():number {
        return Layout.scalePixels(this.bounds.height);
    }

    public get cellWidth():number {
        return Layout.scalePixels(this.bounds.width);
    }


    buildBackground() {
        if (!this.bg && !this.config.symbolAssetsMap[this.id].background) {
            return;
        }

        const textureId =
            this.config.symbolAssetsMap[this.id].background || "sym_empty";
        if (!this.bg) {
            this.bg = this.assets.getSprite(textureId);
            this.bg.anchor.set(0.5, 0.5);
            this.addChildAt(this.bg, 0);
        } else {
            this.bg.texture = this.assets.getTexture(textureId);
        }

        Layout.testSetHeight(this.bg, this.bounds.height);
        if (this.config.symbolBlur) {
            const blurtextureId =
                this.config.symbolAssetsMap[this.id].background_blur ||
                "sym_empty";
            if (!this.bgBlur) {
                this.bgBlur = this.assets.getSprite(blurtextureId);
                this.bgBlur.anchor.set(0.5, 0.5);
                this.addChildAt(this.bgBlur, this.children.indexOf(this.bg));
            } else {
                this.bgBlur.texture = this.assets.getTexture(blurtextureId);
            }

            this.bgBlur.alpha = 0;
            Layout.testSetHeight(this.bgBlur, this.bounds.height);
        }
    }

    setBlurLevel(level: number) {
        if (this.isSpine) {
            this.spineBlurLevel = level;
        } else {
            const inverseLevel = Math.min(1, (2 - level * 2));

            if (this.bg && this.bgBlur)
            {
                this.bg.alpha = inverseLevel;
                this.bgBlur.alpha = level;
            }

            if (this.config.symbolBlur)
            {
                this.symbol.alpha = inverseLevel;
                this.symbolBlur.alpha = level;
            }
        }

    }

    setId(id: number) {
        if (this.isSpine) {
            if (this.fakeId) {
                this.id = this.fakeId;
            } else {
                this.id = id;
            }
            this.spineAnim.setActiveSkin(this.id);
            this.spineAnim.play((this.config.symbolBlur && (this.spineBlurLevel >= 1)) ? "blur" : undefined);
        } else {
            this.id = id;
            const anchor = this.config.symAnchors[id];
            const anchorX = anchor ? anchor.x : 0.5;
            const anchorY = anchor ? anchor.y : 0.5;

            const symSpriteId: number | string = this.config.symbolAssetsMap[
                this.id
            ].name;

            const textureBounds = this.positionDefinitions
                ? this.positionDefinitions.get("sym_" + symSpriteId)
                : undefined;

            this.symbol.anchor.set(anchorX, anchorY);
            this.symbol.texture = this.assets.getTexture("sym_" + symSpriteId);
            if (textureBounds) {
                Layout.testSetWidth(this.symbol, textureBounds.width);
                if (this.id === 0) {
                    // debugger;
                }
                let boundX = this.bounds.x + this.bounds.width / 2;
                let textX = textureBounds.x + textureBounds.width / 2;
                this.symbol.x = Math.ceil(
                    Layout.scalePixels(
                        textX - boundX
                    )
                );
                this.symbol.y = Math.ceil(
                    Layout.scalePixels(
                        -this.bounds.y -
                            this.bounds.height / 2 +
                            textureBounds.y +
                            textureBounds.height / 2
                    )
                );
                // console.log()
            } else {
                ConsoleUtil.warn(
                    "PSD warning! Texture : 'sym_" +
                        symSpriteId +
                        "' doesn't have bounds"
                );
            }
            if (this.config.symbolBlur) {
                if (this.config.skipBlur.indexOf(id) !== -1) {
                    this.symbolBlur.texture = this.assets.getTexture(
                        "sym_" + symSpriteId
                    );
                } else {
                    this.symbolBlur.texture = this.assets.getTexture(
                        "sym_" + symSpriteId + "_blur"
                    );
                }
                this.symbolBlur.anchor.set(anchorX, anchorY);
                if (textureBounds) {
                    Layout.testSetHeight(this.symbolBlur, textureBounds.height);
                    let boundX = this.bounds.x + this.bounds.width / 2;
                    let textX = textureBounds.x + textureBounds.width / 2;
                    Layout.testSetWidth(this.symbol, textureBounds.width);

                    this.symbolBlur.x = Math.ceil(
                        Layout.scalePixels(
                            textX - boundX
                        )
                    );
                    this.symbolBlur.y = Math.ceil(
                        Layout.scalePixels(
                            -this.bounds.y -
                                this.bounds.height / 2 +
                                textureBounds.y +
                                textureBounds.height / 2
                        )
                    );
                }
            }
            // this.symbol.anchor.set(anchorX, anchorY);
            this.buildBackground();
        }
    }

    getId(): number {
        return this.id;
    }

    public hide(animated: boolean = false) {
        if (this.isSpine) {
            this.spineAnim.hide()
        } else {
            if (animated) {
                if (this.symbol) {
                    Tween.to(this.symbol, 0.5, {
                        alpha: 0,
                        onComplete: () => {
                            this.symbol.visible = false;
                            this.symbol.alpha = 1;
                        },
                    });
                }
            } else {
                if (this.symbol) {
                    this.symbol.visible = false;
                }
            }
        }
    }

    public show(animated: boolean = false) {
        if (this.forceHidden) {
            return;
        }

        if (this.isSpine) {
            this.spineAnim.show();
        } else {
            if (animated) {
                if (this.symbol) {
                    this.symbol.alpha = 0;
                    this.symbol.visible = true;
                    Tween.to(this.symbol, 0.5, {
                        alpha: 1,
                    });
                }
            } else {
                if (this.symbol) {
                    this.symbol.visible = true;
                }
            }
        }
    }

    public showWin(loop: boolean = false) {
        if (!this.isSpine) {
            VisualUtil.tintTween(this.symbol, 0xffffff, 0.2);
        }
        this.hide();
    }

    blackening(loop: boolean = false) {
        return VisualUtil.blackeningTween(this.symbol, 0x5a5a5a, 0.8);
       // this.hide();
    }

    setupAnimation(loop: boolean = false, animName = "_anim"): boolean {
        const symSpriteId: number | string = this.config.symbolAssetsMap[
            this.id
        ].name;
        let textureBounds = this.positionDefinitions.get(
            "sym_" + symSpriteId + animName
        );
        if (!textureBounds) {
            textureBounds = this.positionDefinitions.get("sym_" + symSpriteId);
        }
        let animID = animName != "_anim" ? symSpriteId + animName : symSpriteId;

        const animData = this.config.animData[animID];

        if (animData && animData.numFrames) {
            this.hide();
            // this.symAnim.textures = animTextures;
            // this.symAnim.animationSpeed = animTextures.length / 90;
        } else if (animData && animData.fade) {
            // this.symAnim.textures = animTextures;
            // this.symAnim.animationSpeed = animTextures.length / 90;
        } else if (animData && animData.shine) {
            this.symbol.filters = [this.shineFilter];

            this.shineFilter.uniforms.position = 0;
            this.shineFilter.uniforms.intensity = 0;
        }

        return textureBounds != null && animData != null;
    }

    // playHitAnimation(animData: any) {
    //     //TODO Remove and use other play animation method
    //     // const animAnchor = animData.anchor;
    //     // if (animAnchor) {
    //     //     this.symAnim.anchor.set(animAnchor.x, animAnchor.y);
    //     // }
    //     // else {
    //     //     this.symAnim.anchor.set(.5, .5);
    //     // }

    //     // this.symAnim.visible = true;
    //     // this.symAnim.alpha = 1;
    //     // this.symAnim.loop = false;

    //     // const frameData = animData.frames;
    //     // const fadeData = animData.fade;
    //     // if (frameData) {
    //     //     this.symAnim.textures =
    //     //         this.assets.generateFrames(frameData.prefix, frameData.pad, frameData.start, frameData.end);
    //     //     this.symAnim.animationSpeed = animData.speed ? animData.speed : 1;
    //     //     this.symAnim.onComplete = this.hideSymAnim.bind(this);
    //     //     this.symAnim.gotoAndPlay(0);
    //     // }
    //     // else if (fadeData) {
    //     //     const duration = fadeData.duration ? fadeData.duration : .1;
    //     //     this.symAnim.textures = [this.assets.getTexture(fadeData.textureName)];
    //     //     Tween.from(this.symAnim, duration, { alpha: 0, yoyo: true, repeat: 1 });
    //     // }
    // }

    public playAnimation(animName?: string, timeScale = 1): Promise<boolean> {
        if (this.isSpine) {
            return new Promise<boolean>((res) => {
                this.spineAnim.setActiveSkin(this.id)
                if (animName) {
                    if (this.spineAnim.state.hasAnimation(animName)) {
                        this.spineAnim.playAsync(animName, timeScale).then(() => {
                            res()
                        });
                        this.show();
                    } else if (this.spineAnim.hasChainingAnim(animName)) {
                        this.spineAnim.playChainingAnim(animName, timeScale).then(() => {
                            res()
                        });
                        // this.spineAnim.playAsyncChainingStates(animName, timeScale).then(() => {
                        //     res()
                        // });
                        this.show();
                    } else {
                        // this.show()
                        console.warn(
                            `[SPINE ANIMATION] -> Spine <${this.spineAnim.animConfig.name}> animation <${animName}> is missing from states: <${this.spineAnim.animConfig.states}> or chaining states <${this.spineAnim.animConfig.getSkinConfig(this.spineAnim.activeSkin)?.chainingStates}> animation! `,
                        );
                        res()
                    }
                } else {
                    this.spineAnim.setActiveSkin(this.id)
                    let hasPlayDefault = this.spineAnim.animConfig.getSkinConfig(this.spineAnim.activeSkin).playDefault;
                    if (hasPlayDefault) {
                        this.spineAnim.playAsync(undefined, timeScale).then(() => {
                            res();
                        })
                        this.show();
                    } else {
                        res();
                    }
                }
            })
        } else {
            return new Promise((res) => {
                let symSpriteId: number | string = this.config.symbolAssetsMap[
                    this.id
                ].name;
                if (animName && animName != "_anim") {
                    symSpriteId = symSpriteId + animName;
                }
                const animData = this.config.animData[symSpriteId];
                let resComplete = () => res(true);

                if (animData.shine) {
                    Tween.to(this.shineFilter.uniforms, 1.5, {
                        position: 1,
                        yoyo: true,
                        repeat: animData.loop ? -1 : 3,
                        onComplete: resComplete,
                    });
                    Tween.to(this.shineFilter.uniforms, 0.2, {
                        intensity: 2,
                        onComplete: resComplete,
                    });
                    if (!animData.loop) {
                        Tween.to(this.shineFilter.uniforms, 0.2, {
                            intensity: 0,
                            delay: 6,
                            onComplete: resComplete,
                        });
                    }
                } else {
                    res(true);
                }
            });
        }
    }

    fade() {
        if (!this.isSpine) {
            this.show();
            this.symbol.filters = [];
            VisualUtil.tintTween(this.symbol, this.config.symbolTintColor, 0.2);
        }
    }

    stopAnimation() {
        if (this.isSpine) {
            this.spineAnim.playAsync();
        } else {
            this.show();
            if (this.shineFilter) {
                Tween.killTweensOf(this.shineFilter.uniforms);

                const hideTime = 0.2;

                if (this.symbol.filters.indexOf(this.shineFilter) !== -1) {
                    Tween.to(this.shineFilter.uniforms, hideTime, {
                        intensity: 0,
                        onComplete: this.removeFilters,
                        callbackScope: this,
                    });
                }
            }
        }
    }

    hideWin() {
        VisualUtil.tintTween(this.isSpine ? this.spineAnim : this.symbol, 0xffffff, 0.2);
        this.show();
    }

    removeFilters() {
        this.symbol.filters = [];
    }

    getReelIdx() {
        return this.reelIdx;
    }

    public clone() {
        const sym = new ReelSymbol();
        sym.create(this.bounds, this.positionDefinitions);
        sym.reelIdx = this.reelIdx;
        sym.x = this.x;
        sym.y = this.y;
        sym.setId(this.id);

        return sym;
    }
}

export default ReelSymbol;
